<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import BaseDropdown from '~common/base-dropdown.vue'
import { useCommon } from '~/composables/common/use-common'

const { openLiveChat } = useCommon()

withDefaults(
  defineProps<{
    agencyName: string;
    accounts?: string[],
    creditRate: number;
    iconClass: string;
    color?: 'green' | 'yellow' 
  }>(),
  { accounts: undefined, color: 'green' }
)

const noteAccountRef = ref<HTMLElement>()
const isInstructionOpen = ref(false)

const toggleOpenInstruction = () => isInstructionOpen.value = !isInstructionOpen.value

watch(() => noteAccountRef.value, (noteRef) => {
  if (noteRef) {
    onClickOutside(noteAccountRef, () => isInstructionOpen.value = false)
  }
}, { immediate: true })
</script>

<template>
  <div :class="['agency-wallet', { 'agency-wallet--yellow': color === 'yellow' }]">
    <div class="agency-wallet__title">
      <div class="agency-wallet__title__icon"><span :class="iconClass"></span></div>

      <div class="agency-wallet__title__name">Ví {{ agencyName }}</div>

      <div class="agency-wallet__title__tooltip">
        <img class="agency-wallet__title__tooltip__icon" src="~assets/images/icons/info.svg" alt="info" />
        <div class="agency-wallet__title__tooltip__content">
          <p>Cược trung gian <strong>{{ creditRate }}K/1 Điểm</strong>.</p>
          <p>
            Để thay đổi tỉ lệ xin vui lòng
            <a class="agency-wallet__title__tooltip__link" @click="openLiveChat">Liên hệ CSKH</a>
          </p>
        </div>
      </div>
    </div>

    <BaseDropdown
      v-if="accounts"
      :optionItems="accounts.map(account => ({ label: account, value: account }))"
      :valueSelected="accounts[0]"
    />

    <a v-else class="agency-wallet__instructions__button" @click="toggleOpenInstruction">Hướng dẫn tạo ví</a>
    <div v-if="isInstructionOpen" ref="noteAccountRef" class="agency-wallet__instructions">
      <div class="agency-wallet__instructions__head">
        <div class="agency-wallet__instructions__title">Tạo tài khoản ví {{ agencyName }}</div>
        <span class="icon-close" @click="toggleOpenInstruction"></span>
      </div>
      <p class="agency-wallet__instructions__step"><strong>Bước 1:</strong> Nạp tiền vào ví chính</p>
      <p class="agency-wallet__instructions__step">
        <strong>Bước 2:</strong> Chuyển tiền vào ví đại lý {{ agencyName }} mới
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.agency-wallet {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  position: relative;
  margin-top: 8px;
  padding: 8px;
  border-radius: 6px;
  background-color: $white;

  &__title {
    display: flex;
    align-items: center;
    gap: 4px;

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 32px;
      height: 32px;
      border-radius: 50%;
      background-color: $green-500;
      filter: drop-shadow(0 4px 10px rgba(17, 28, 3, 0.2));

      span::before {
        font-size: 20px;
        color: $white;
      }
    }

    &__name {
      margin-top: 3px;
      text-wrap: nowrap;
      font-weight: 600;
    }

    &__tooltip {
      position: relative;
      flex: 0 0 18px;

      &__content {
        visibility: hidden;
        opacity: 0;
        position: absolute;
        bottom: calc(100% + 15px);
        left: 50%;
        transform: translateX(-50%);
        z-index: 9;
        width: max-content;
        padding: 2px 8px;
        border-radius: 4px;
        line-height: 24px;
        color: $white;
        background-color: $neutral-700;
        box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
        transition: opacity 0.3s;

        &:after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          margin-left: -8px;
          border-width: 8px;
          border-style: solid;
          border-color: $neutral-700 transparent transparent transparent;
        }
      }

      &__link {
        cursor: pointer;
        text-decoration: underline;
        color: $yellow-500;
      }

      &:hover .agency-wallet__title__tooltip__content {
        visibility: visible;
        opacity: 1;
      }
    }
  }

  &:deep(.base-dropdown__header) {
    width: 120px;
    padding: 3px 7px;
    color: $green-900;
    background-color: $green-100;
    border-color: $green-300;
    font-size: 12px;
    font-weight: 700;

    &.arrow-drop-down::after {
      top: 3px;
      right: 9px;
      color: $green-600;
    }
  }

  &__instructions {
    &__button {
      cursor: pointer;
      text-decoration: underline;
    }

    position: absolute;
    top: -32px;
    left: calc(100% + 9px);
    z-index: 2;
    width: 391px;
    padding: 12px;
    border-radius: 6px;
    background-color: $white;
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.14);

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: -16px;
      transform: translateY(-50%);
      border-width: 8px;
      border-style: solid;
      border-color: transparent $white transparent transparent;
    }

    &__head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px solid $neutral-200;
    }

    &__title {
      font-size: 16px;
      font-weight: 700;
    }

    .icon-close::before {
      font-size: 20px;
      cursor: pointer;
    }
    
    &__step {
      line-height: 24px;

      strong {
        font-weight: 700;
        color: $primary-color;
      }
    }
  }

  &--yellow {
    .agency-wallet__title__icon {
      background-color: $yellow-500;
    }

    &:deep(.base-dropdown__header) {
      background-color: #fff6d9;
      border-color: #ffe38e;
    }
  }
}
</style>
